import { useEffect, useRef } from 'react';

type DeferredEffectOptions = {
  /**
   * Minimum delay in milliseconds before executing the effect.
   * This ensures the effect runs after the specified delay, even if
   * requestIdleCallback fires earlier.
   * @default 0
   */
  minDelayMs?: number;
  
  /**
   * Timeout for requestIdleCallback. If the browser doesn't have idle time
   * within this period, the callback will be forced.
   * @default 2000
   */
  timeoutMs?: number;
};

/**
 * A hook that defers effect execution until after the browser is idle.
 * Uses requestIdleCallback with a setTimeout fallback for browsers that
 * don't support it.
 * 
 * This is useful for non-critical effects that shouldn't block initial render,
 * such as:
 * - Analytics initialization
 * - Non-essential logging
 * - Background data prefetching
 * - Streak counter initialization
 * 
 * The effect still runs and cleans up properly, just deferred.
 * 
 * @param effect - The effect function, same as useEffect. Can return a cleanup function.
 * @param deps - Dependency array, same as useEffect.
 * @param options - Configuration options for deferral behavior.
 * 
 * @example
 * ```tsx
 * // Defer analytics initialization
 * useDeferredEffect(() => {
 *   initializeAnalytics();
 * }, []);
 * 
 * // Defer with minimum delay
 * useDeferredEffect(() => {
 *   logPageView(pageId);
 * }, [pageId], { minDelayMs: 100 });
 * ```
 */
export function useDeferredEffect(
  effect: () => void | (() => void),
  deps: React.DependencyList,
  options: DeferredEffectOptions = {}
): void {
  const { minDelayMs = 0, timeoutMs = 2000 } = options;
  
  // Track cleanup function from the effect
  const cleanupRef = useRef<(() => void) | void>(undefined);
  
  useEffect(() => {
    let isCancelled = false;
    let idleCallbackId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let minDelayTimeoutId: ReturnType<typeof setTimeout> | undefined;
    
    const runEffect = () => {
      if (isCancelled) return;
      
      // Clean up previous effect if it exists
      if (typeof cleanupRef.current === 'function') {
        cleanupRef.current();
      }
      
      // Run the effect and store its cleanup
      cleanupRef.current = effect();
    };
    
    const scheduleEffect = () => {
      if (isCancelled) return;
      
      // Use requestIdleCallback if available, otherwise setTimeout
      if ('requestIdleCallback' in window) {
        idleCallbackId = window.requestIdleCallback(runEffect, { timeout: timeoutMs });
      } else {
        // Fallback: use setTimeout with 0 to defer to next tick
        timeoutId = setTimeout(runEffect, 0);
      }
    };
    
    // If minDelayMs is set, wait at least that long before scheduling
    if (minDelayMs > 0) {
      minDelayTimeoutId = setTimeout(scheduleEffect, minDelayMs);
    } else {
      scheduleEffect();
    }
    
    // Cleanup function
    return () => {
      isCancelled = true;
      
      // Cancel any pending callbacks
      if (idleCallbackId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      if (minDelayTimeoutId !== undefined) {
        clearTimeout(minDelayTimeoutId);
      }
      
      // Run effect cleanup if it exists
      if (typeof cleanupRef.current === 'function') {
        cleanupRef.current();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * A hook that defers effect execution until after the first user interaction.
 * Useful for effects that are truly optional until the user engages with the page.
 * 
 * @param effect - The effect function, same as useEffect.
 * @param deps - Dependency array, same as useEffect.
 * 
 * @example
 * ```tsx
 * useDeferredUntilInteraction(() => {
 *   // This runs after first click, touch, or keypress
 *   loadHeavyModule();
 * }, []);
 * ```
 */
export function useDeferredUntilInteraction(
  effect: () => void | (() => void),
  deps: React.DependencyList
): void {
  const hasInteractedRef = useRef(false);
  const cleanupRef = useRef<(() => void) | void>(undefined);
  const effectRef = useRef(effect);
  
  // Keep effect ref up to date
  useEffect(() => {
    effectRef.current = effect;
  });
  
  useEffect(() => {
    // If already interacted in a previous render, run immediately
    if (hasInteractedRef.current) {
      cleanupRef.current = effectRef.current();
      return () => {
        if (typeof cleanupRef.current === 'function') {
          cleanupRef.current();
        }
      };
    }
    
    const handleInteraction = () => {
      if (hasInteractedRef.current) return;
      hasInteractedRef.current = true;
      
      // Remove listeners
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      
      // Run the effect
      cleanupRef.current = effectRef.current();
    };
    
    // Listen for first interaction
    window.addEventListener('click', handleInteraction, { once: true, passive: true });
    window.addEventListener('touchstart', handleInteraction, { once: true, passive: true });
    window.addEventListener('keydown', handleInteraction, { once: true, passive: true });
    
    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      
      if (typeof cleanupRef.current === 'function') {
        cleanupRef.current();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
